import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import { useContext } from "react";

import RadioInput from "@stadium/ui/src/components/radio-input/radio-input";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";
import BsDropdown from "@stadium/ui/src/components/dropdown/bs-dropdown";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useProductDetailContext } from "@contexts/product-detail-context";

import { isValidHexCode } from "@stadium/helpers";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_ATTRIBUTE } from "@constants/analytics/shop";

import { OPTIONTYPE_COLOR_KEY, OPTIONTYPE_SIZE_KEY } from "@constants/products/detail";

import styles from "./main.module.scss";

const MOBILE_DROPDOWN_THRESHOLD = 8;

const SwagOptionsSelector = ({
  title,
  availData,
  selectedData,
  handleChange,
  disabled = false,
  productId,
  variantSelected,
  checkOutOfStock,
  disabledIfOutOfStock = false,
  errorClassName = "",
  showImageOptions = false,
  eventLocationAnalytics = {},
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const {
    isColorSelected,
    colorSelectionError: showErrorMessage,
    setIsColorSelected,
  } = useProductDetailContext();

  const withAnalytics = useWithAnalytics();

  const shouldShowDropdown =
    isMobileDevice &&
    availData.length > MOBILE_DROPDOWN_THRESHOLD &&
    availData[0].optionTypeKey === OPTIONTYPE_COLOR_KEY;

  const renderLabelContent = (data) => {
    const isOptionTypeSize = OPTIONTYPE_SIZE_KEY.includes(data.optionTypeKey);
    const isColorOutOfStock = data.colorOutOfStock;
    const isColorOutOfStockError = data.id === selectedData.id;
    const radioColor = isValidHexCode(data.name) ? data.name : "transparent";

    return (
      <ToolTip
        content={(data.colorOutOfStock && "Out of Stock") || data.presentation}
        isLightTheme={true}
        placement="top-start"
        arrow={true}
        customClassName={styles.colorVariantTippy}
        childrenWrapperClassName={isColorOutOfStock ? styles.outOfStockSku : ""}
      >
        {!isOptionTypeSize && showImageOptions ? (
          <Multimedia
            type="image"
            src={data.image}
            alt={data.presentation}
            height={66}
            width={66}
            isNextImage={false}
            className={isColorOutOfStock ? styles.disabledImg : ""}
          />
        ) : (
          <div
            className={classnames("", {
              [styles.roundOption]: true,
              "t-br-1 activeRadio": isColorOutOfStockError && !isColorOutOfStock,
              nonActive: data.id !== selectedData.id,
              disabled: isColorOutOfStock,
              "t-cl-1": isColorOutOfStockError,
              disabledColor: !isColorOutOfStockError && isColorOutOfStock,
            })}
            aria-label={title}
          >
            <span
              className={`${styles.roundOptionSpan} ${
                isOptionTypeSize ? styles["rdWidth"] : styles["rdBorder"]
              }`}
              style={{
                backgroundColor: isOptionTypeSize ? "#eff0f2" : radioColor,
              }}
            >
              {isOptionTypeSize ? data.presentation : ""}
            </span>
          </div>
        )}
      </ToolTip>
    );
  };

  const labelContentSelector = (data) => {
    return renderLabelContent(data);
  };

  const isColorOutOfStock = (data) => {
    if (!isEmpty(data.variants)) {
      const isAllSizesOutOfStock = data.variants.every(({ outOfStock }) => outOfStock);
      if (isAllSizesOutOfStock) return true;
      // if there is size variant selected and we don't have a that variant than show it as a OOS
      return (
        data.variants.find(({ id }) => id === variantSelected?.id)?.outOfStock ??
        !isEmpty(variantSelected)
      );
    } else {
      return data.outOfStock;
    }
  };

  const dropDownOptionClickHandler = (selectedVal) => {
    setIsColorSelected(true);

    if (eventLocationAnalytics.track) {
      withAnalytics(undefined, {
        ...eventLocationAnalytics,
        target: eventLocationAnalytics?.target ?? ANALYTICS_TARGET_ATTRIBUTE,
        action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_SELECTED,
        eventProperties: {
          product_id: eventLocationAnalytics.eventProperties?.product_id,
          attribute_type: eventLocationAnalytics.eventProperties?.attribute_type,
          attribute_value: selectedVal.data.presentation,
          ...eventLocationAnalytics.eventProperties,
        },
      })();
    }

    return handleChange(selectedVal.data);
  };

  const renderDropDownLabelContent = (data) => {
    return (
      <div className={styles.ddOption}>
        <Multimedia
          className={styles.ddImage}
          type="image"
          src={data.image}
          alt={data.presentation}
          height={68}
          width={68}
        />
        <p>{data.presentation}</p>
        {data.outOfStock && <p className="text-danger">Out of Stock</p>}
      </div>
    );
  };

  const renderOptionsSelector = () => {
    if (shouldShowDropdown) {
      const dropdownOptions = availData.map((data) => ({
        id: data.id,
        label: renderDropDownLabelContent(data),
        data,
      }));
      return (
        <BsDropdown
          id="swag-options-selector"
          placeHolder="Select"
          options={dropdownOptions}
          renderHeadValue={(option) => renderDropDownLabelContent(option.data)}
          onChange={dropDownOptionClickHandler}
          customClass={styles.bsDropdown}
          toggleBtnClass={styles.rdDropdownToggle}
          disabled={disabled}
          dropdownClass={styles.sizeDropdownMenuV2}
          toggleArrowClass={styles.iconChevronDown}
          renderDropdownAsBottomModal
          parentLabel="Color"
          parentLabelClass={styles.parentLabel}
        />
      );
    }
    return (
      <div className={`${styles.dFlexRd} ${styles.readOnlyText} checkedThemeBorder`}>
        {availData.map((data) => {
          const colorOutOfStock =
            typeof checkOutOfStock == "function" ? checkOutOfStock(data) : isColorOutOfStock(data);
          return (
            <RadioInput
              value={data.id}
              disabled={disabled || (disabledIfOutOfStock && colorOutOfStock)}
              id={`${data.id}-${productId}`}
              checked={data.id === selectedData.id}
              name={`${data.optionTypeKey}-${productId}`}
              key={`${data.name}-${data.id}-${data.variantId}`}
              onChange={() => {
                handleChange(data);
                setIsColorSelected(true);

                if (eventLocationAnalytics.track) {
                  withAnalytics(undefined, {
                    ...eventLocationAnalytics,
                    target: eventLocationAnalytics?.target ?? ANALYTICS_TARGET_ATTRIBUTE,
                    action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_SELECTED,
                    eventProperties: {
                      product_id: eventLocationAnalytics.eventProperties?.product_id,
                      attribute_type: eventLocationAnalytics.eventProperties?.attribute_type,
                      attribute_value: data.presentation,
                      ...eventLocationAnalytics.eventProperties,
                    },
                  })();
                }
              }}
              label={{
                for: { id: `${data.id}-${productId}` },
                classes: `labelText ${colorOutOfStock ? styles.outOfStockLabel : ""}`,
                content: labelContentSelector({ ...data, colorOutOfStock }),
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`${styles.colorsContainer} ${styles.rdSelector} rdSelector ${styles.withRevamp}`}
    >
      <div className={`${styles.title} ${styles.primaryColor} `}>
        {shouldShowDropdown ? "Color" : title}
      </div>
      {renderOptionsSelector()}
      {showErrorMessage && !isColorSelected && (
        <div className={`${styles.addToBagBtnError} ${styles.colorError} ${errorClassName}`}>
          Please select a color.
        </div>
      )}
      {isColorSelected && isEmpty(selectedData.variants) && selectedData.outOfStock && (
        <div className={`${styles.addToBagBtnError} ${styles.colorError} ${errorClassName}`}>
          Out of stock, please select another color
        </div>
      )}
    </div>
  );
};

export default SwagOptionsSelector;
